package com.arena_web.ui.components.overlay

import androidx.compose.runtime.Composable
import com.arena_web.ui.components.icons.Icon
import com.arena_web.ui.components.widgets.HiddenScrollbarStyle
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.sections.profile.edit.EDIT_MODAL_SURFACE
import com.arena_web.ui.sections.profile.edit.GradientBorderRing
import com.arena_web.ui.sections.profile.edit.InterestOption
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.AppTypography
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.*

/**
 * Read-only preference-details popup (Figma 7844:341021). Shown when a user
 * clicks an interest chip in their profile that has a saved note — surfaces
 * the chip + the free-form note text.
 */
@Composable
fun DetailsPreferenceDialog(
    option: InterestOption?,
    note: String,
    onDismiss: () -> Unit
) {
    Box(
        modifier = Modifier
            .position(Position.Fixed)
            .top(0.px).left(0.px).right(0.px).bottom(0.px)
            .zIndex(1100)
            .backgroundColor(Color("rgba(2, 4, 11, 0.7)"))
            .onClick { onDismiss() },
        contentAlignment = Alignment.Center
    ) {
        Column(
            modifier = Modifier
                .sBorderRadius(26)
                .sPadding(18)
                .sGap(22)
                .onClick { it.stopPropagation() }
                .then(HiddenScrollbarStyle.toModifier())
                .styleModifier {
                    property("width", "min(${s(377)}, 92vw)")
                    property("max-height", "90vh")
                    property("overflow-y", "auto")
                    property("overflow-x", "hidden")
                    property("background-color", EDIT_MODAL_SURFACE)
                    property("box-shadow", "0 24px 64px rgba(0, 0, 0, 0.5)")
                }
        ) {
            // Header: chip + " — " separator + "Preference details" title
            Row(
                modifier = Modifier.fillMaxWidth().sGap(8),
                verticalAlignment = Alignment.CenterVertically
            ) {
                if (option != null) {
                    ReadOnlyInterestChip(option)
                }
                SpanText(
                    "—",
                    modifier = Modifier
                        .sFontSize(20)
                        .color(AppColors.Text.Secondary)
                )
                SpanText(
                    LocalAppStrings.current.preferenceDetailsTitle,
                    modifier = Modifier
                        .sFontSize(20)
                        .fontWeight(AppTypography.Medium)
                        .sLineHeight(26)
                        .sLetterSpacing(0.2)
                        .color(AppColors.Text.Secondary)
                )
            }

            // Description box — Figma 7861:341052: rounded 12, gradient border via
            // AppGradients.ButtonBorderGradientPrimary (mask-composite overlay),
            // top→bottom gradient transparent → rgba(48,108,206,0.2), padding on
            // inner Box so the border overlay covers the full visible area.
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .sMinHeight(60)
                    .sBorderRadius(12)
                    .styleModifier {
                        property("position", "relative")
                        property(
                            "background",
                            "linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(48, 108, 206, 0.2) 100%)"
                        )
                    }
            ) {
                GradientBorderRing(radiusPx = 12)
                Box(
                    modifier = Modifier
                        .fillMaxSize()
                        .styleModifier {
                            property("padding", "${s(8)} ${s(12)}")
                            property("display", "flex")
                            property("align-items", "center")
                        }
                ) {
                    SpanText(
                        note,
                        modifier = Modifier
                            .sFontSize(14)
                            .fontWeight(AppTypography.Regular)
                            .sLineHeight(22)
                            .sLetterSpacing(0.028)
                            .color(AppColors.Text.Secondary)
                    )
                }
            }
        }
    }
}

@Composable
private fun ReadOnlyInterestChip(option: InterestOption) {
    Box(
        modifier = Modifier
            .sHeight(32)
            .sBorderRadius(22)
            .styleModifier {
                property("position", "relative")
                property(
                    "background",
                    "linear-gradient(0deg, rgba(12, 66, 255, 0.7) 3%, rgba(102, 102, 102, 0) 60%)"
                )
                property("display", "inline-flex")
                property("align-items", "center")
            }
    ) {
        GradientBorderRing(radiusPx = 22)
        Row(
            modifier = Modifier
                .sGap(8)
                .styleModifier {
                    property("padding", "${s(7)} ${s(14)}")
                    property("display", "inline-flex")
                    property("align-items", "center")
                },
            verticalAlignment = Alignment.CenterVertically
        ) {
            if (option.iconSrc != null) {
                Icon(src = option.iconSrc, modifier = Modifier.sSize(16))
            }
            SpanText(
                option.label,
                modifier = Modifier
                    .sFontSize(16)
                    .fontWeight(AppTypography.Medium)
                    .sLineHeight(18)
                    .sLetterSpacing(0.032)
                    .color(AppColors.Text.Secondary)
            )
        }
    }
}
